

<template>
  <div class="form-control w-full" :class="rootClasses">
    <input
      v-if="type !== 'textarea'"
      ref="input"
      class="input input-bordered w-full"
      :class="[inputClasses, customClass]"
      :type="newType"
      :autocomplete="newAutocomplete"
      :maxlength="maxlength"
      :name="name"
      v-model="computedValue"
      v-bind="$attrs"
      @focus="onFocus"
      @blur="onBlur"
    >

    <textarea
      v-else
      ref="textarea"
      class="textarea textarea-bordered w-full resize-none"
      style="min-height: 120px; max-height: 600px;"
      :class="[inputClasses, customClass]"
      :maxlength="maxlength"
      :name="name"
      v-model="computedValue"
      v-bind="$attrs"
      @focus="onFocus"
      @blur="onBlur"
    />

    <d-icon 
      v-if="icon" 
      class="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/70 pointer-events-none z-10" 
      :icon="icon" 
      :pack="iconPack" 
      size="lg" 
    />

    <d-icon
      v-if="passwordReveal && !loading"
      class="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer hover:text-primary"
      :icon="passwordVisibleIcon"
      :pack="iconPack"
      size="lg"
      @click="togglePasswordVisibility"
    />

    <d-icon
      v-else-if="!loading && statusType"
      class="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
      :icon="statusTypeIcon"
      :pack="iconPack"
      size="lg"
      :custom-class="[statusColorIcon]"
    />

    <small v-if="maxlength && hasCounter && type !== 'number'" class="label-text-alt mt-1" :class="{ 'opacity-0': !isFocused }">
      {{ valueLength }} / {{ maxlength }}
    </small>
  </div>
</template>

<script>
import DIcon from '@components/DIcon.vue';
import FormElementMixin from '@mixins/formElementMixin';

export default {
  name: 'DInput',
  components: {
    DIcon,
  },
  mixins: [FormElementMixin],
  inheritAttrs: false,
  emits: ['update:modelValue', 'focus', 'blur'],
  props: {
    modelValue: [Number, String],
    type: {
      type: String,
      default: 'text',
    },
    passwordReveal: Boolean,
    hasCounter: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: String,
      default: '',
    },
    name: String,
    ghost: Boolean,
    color: {
      type: String,
      default: '',
      validator: (val) => ['', 'neutral', 'primary', 'secondary', 'accent', 'info', 'success', 'warning', 'error'].includes(val)
    },
    inputSize: {
      type: String,
      default: '',
      validator: (val) => ['', 'xs', 'sm', 'md', 'lg'].includes(val)
    },
    bordered: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      newAutocomplete: this.autocomplete || false,
      isPasswordVisible: false,
      _elementRef: this.type === 'textarea' ? 'textarea' : 'input',
    };
  },
  computed: {
    newType() {
      if (this.passwordReveal && this.type === 'password') {
        return this.isPasswordVisible ? 'text' : 'password';
      }
      return this.type;
    },
    
    computedValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },

    rootClasses() {
      return [
        {
          'relative': this.icon || this.hasIconRight,
        },
      ];
    },
    
    inputClasses() {
      const element = this.type === 'textarea' ? 'textarea' : 'input';
      const classes = [];
      
      // Style variants
      if (this.ghost) {
        classes.push(`${element}-ghost`);
      }
      
      // Remove border if needed
      if (!this.bordered) {
        classes.push('border-none');
      }
      
      // Colors
      if (this.color) {
        classes.push(`${element}-${this.color}`);
      }
      
      // Status colors based on validation
      if (this.statusDaisyUIClass) {
        classes.push(this.statusDaisyUIClass);
      }
      
      // Sizes
      if (this.inputSize) {
        classes.push(`${element}-${this.inputSize}`);
      }
      
      // Icon padding
      if (this.icon) {
        classes.push('pl-10');
      }
      if (this.hasIconRight) {
        classes.push('pr-10');
      }
      
      // Maintain rounded prop compatibility
      if (this.rounded) {
        classes.push('rounded-full');
      }
      
      // Loading state
      if (this.loading) {
        classes.push('loading');
      }
      
      return classes;
    },
    
    hasIconRight() {
      return this.passwordReveal || this.loading || this.statusType;
    },

    /**
     * Status based on validation transformed to daisyUI classes
     */
    statusDaisyUIClass() {
      const element = this.type === 'textarea' ? 'textarea' : 'input';
      switch (this.statusType) {
        case 'is-success': return `${element}-success`;
        case 'is-error': return `${element}-error`;
        case 'is-info': return `${element}-info`;
        case 'is-warning': return `${element}-warning`;
        default: return '';
      }
    },

    /**
     * Icon name based on the type.
     */
    statusTypeIcon() {
      switch (this.statusType) {
        case 'is-success': return 'check';
        case 'is-error': return 'exclamation-circle';
        case 'is-info': return 'info-circle';
        case 'is-warning': return 'exclamation-triangle';
        default: return null;
      }
    },

    /**
     * Icon color based on the type.
     */
    statusColorIcon() {
      switch (this.statusType) {
        case 'is-success': return 'text-success';
        case 'is-error': return 'text-error';
        case 'is-info': return 'text-info';
        case 'is-warning': return 'text-warning';
        default: return 'text-base-content/70';
      }
    },

    /**
     * Check if have any message prop from parent if it's a Field.
     */
    hasMessage() {
      return !!this.statusMessage;
    },
    
    /**
     * Current password-reveal icon name.
     */
    passwordVisibleIcon() {
      return !this.isPasswordVisible ? 'eye' : 'eye-off';
    },
    
    /**
     * Get value length
     */
    valueLength() {
      if (typeof this.computedValue === 'string') {
        return this.computedValue.length;
      } 
      if (typeof this.computedValue === 'number') {
        return this.computedValue.toString().length;
      }
      return 0;
    },
  },

  methods: {
    /**
     * Toggle the visibility of a password-reveal input
     * by changing the type and focus the input right away.
     */
    togglePasswordVisibility() {
      if (!this.passwordReveal) return;
      
      this.isPasswordVisible = !this.isPasswordVisible;
      
      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.focus();
        }
      });
    },
  },
};
</script> 